import React, { useEffect, useState } from "react";
import { AppointmentMessagingStatus } from "../components/AppointmentMessagingStatus";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDoctorName } from "../lib/displayNames";
import { Mascot } from "../components/Mascot";
import { MediGenieLogo } from "../components/MediGenieLogo";
import { Calendar, Clock, MapPin, Phone, Stethoscope, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { localCalendarDay } from "../lib/localCalendarDay";
import { normalizeBookingGender } from "../lib/bookingGender";
import { branchTimezone, formatSlotTime, formatSlotDateTime, slotDurationMinutes } from "../lib/branchTime";

export default function PublicBooking({ embedded = false, preselectedServiceId = null, preselectedDoctorId = null }) {
  const { slug } = useParams();
  const {user}=useAuth();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [serviceId, setServiceId] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [pat, setPat] = useState({ name: "", phone: "", email: "", gender: "Male", consent: false });
  const [confirmation, setConfirmation] = useState(null);
  const [booking, setBooking] = useState(false);
  const [consultationMode, setConsultationMode] = useState("");

  useEffect(() => {
    if (String(user?.role || "").toLowerCase() !== "patient") return;
    let active = true;
    Promise.allSettled([
      api.get("/auth/me"),
      api.get("/clinic/patients-x/me"),
    ]).then((responses) => {
      if (!active) return;
      const me = responses[0].status === "fulfilled" ? responses[0].value.data : {};
      const profile = responses[1].status === "fulfilled" ? responses[1].value.data : {};
      setPat((current) => ({
        ...current,
        name: profile.name || me.name || user.name || "",
        phone: profile.phone || me.phone || user.phone || "",
        email: profile.email || me.email || user.email || "",
        gender: profile.gender ? normalizeBookingGender(profile.gender) : current.gender,
      }));
    });
    return () => { active = false; };
  }, [user?.user_id, user?.role, user?.name, user?.phone, user?.email]);

  const openPatientDetails = async (selectedSlot) => {
    setSlot(selectedSlot);
    if (String(user?.role || "").toLowerCase() === "patient") {
      setPat((current) => ({ ...current, name: current.name || user.name || "", phone: current.phone || user.phone || "", email: current.email || user.email || "" }));
      const [meResult, profileResult] = await Promise.allSettled([
        api.get("/auth/me"),
        api.get("/clinic/patients-x/me"),
      ]);
      const me = meResult.status === "fulfilled" ? meResult.value.data : {};
      const profile = profileResult.status === "fulfilled" ? profileResult.value.data : {};
      setPat((current) => ({
        ...current,
        name: profile.name || me.name || current.name,
        phone: profile.phone || me.phone || current.phone,
        email: profile.email || me.email || current.email,
        gender: profile.gender ? normalizeBookingGender(profile.gender) : current.gender,
      }));
    }
    setStep(4);
  };

  useEffect(() => {
    if (preselectedServiceId) {
      setServiceId(preselectedServiceId);
      if (!preselectedDoctorId) setStep(2);
    }
    if (preselectedDoctorId) {
      setDoctorId(preselectedDoctorId);
      if (preselectedServiceId) setStep(3);
      else setStep(1);
    }
  }, [preselectedServiceId, preselectedDoctorId]);

  useEffect(() => {
    api.get(`/appointments/public/clinic/${slug}`).then((r) => {
      setData(r.data);
      setDate(localCalendarDay(Date.now(), branchTimezone(r.data.clinic?.timezone)));
    }).catch(() => toast.error("Clinic not found"));
  }, [slug]);

  useEffect(() => {
    if (step === 3 && doctorId && serviceId && date) {
      setSlot(null); setSlots([]); setLoadingSlots(true);
      api.get("/appointments/public/slots", { params: { slug, doctor_id: doctorId, service_id: serviceId, date } })
        .then((r) => setSlots(r.data.slots || []))
        .catch(e=>{setSlots([]);toast.error(e?.response?.data?.detail||"Slots could not be loaded");})
        .finally(() => setLoadingSlots(false));
    }
  }, [step, doctorId, serviceId, date, slug]);

  const book = async () => {
    if (!pat.consent) return toast.error("Please accept the consent to proceed");
    setBooking(true);
    try {
      const r = await api.post(`/appointments/${user?.role==="patient"?"patient-book":"public/book"}?slug=${slug}`, {
        doctor_id: doctorId, service_id: serviceId,
        appointment_datetime: slot.start,
        patient_name: pat.name, patient_phone: pat.phone, patient_email: pat.email,
        patient_gender: pat.gender, consent: true,
        consultation_mode: selectedService?.service_mode === "hybrid" ? consultationMode : null,
      });
      setConfirmation(r.data);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Mascot size={80} className="mg-float" /></div>;
  const { clinic, services, doctors } = data;
  const timezone = branchTimezone(clinic.timezone);
  const onlineBookingAvailable = data.online_booking_available !== false && services.length > 0;
  const selectedService = services.find((s) => s.service_id === serviceId);
  const selectedDoctor = doctors.find((d) => d.doctor_id === doctorId);
  const selectedPrice = selectedDoctor?.service_fees?.[serviceId] ?? selectedService?.price ?? 0;
  
  const filteredDoctors = serviceId
    ? doctors.filter((d) => (d.services || []).includes(serviceId))
    : doctors;

  const filteredServices = preselectedDoctorId
    ? services.filter((s) => {
        const d = doctors.find((doc) => doc.doctor_id === preselectedDoctorId);
        return d && (d.services || []).includes(s.service_id);
      })
    : services;

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-[#F8FAFC]"}>
      {!embedded && (
        <>
          <nav className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between sticky top-0 z-30">
            <MediGenieLogo />
            <div className="text-sm text-slate-500 hidden md:block">Booking — {clinic.name}</div>
          </nav>
          {/* Clinic hero */}
          <section className="mg-gradient-soft px-6 py-10">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 mg-gradient-primary rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold shadow-md">
                {clinic.name.charAt(0)}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-extrabold text-mg-navy">{clinic.name}</h1>
                <div className="mt-2 text-sm text-slate-600 flex flex-col sm:flex-row sm:gap-4 space-y-1 sm:space-y-0">
                  <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {clinic.address || "Address not provided"}</div>
                  <div className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" /> {clinic.phone || "Phone not provided"}</div>
                  {clinic.email && <div className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-slate-400" /> {clinic.email}</div>}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Booking flow */}
      <section className={embedded ? "w-full" : "max-w-3xl mx-auto px-6 py-10"}>
        {!onlineBookingAvailable ? (
          <div className="mg-card border-amber-200 bg-amber-50 text-center" data-testid="booking-unavailable">
            <h2 className="text-xl font-extrabold text-mg-navy">Online booking unavailable</h2>
            <p className="mt-2 text-sm text-slate-600">This clinic is not accepting online appointments right now. Please contact the clinic directly for assistance.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3 text-sm font-semibold">
              {clinic.phone && <a className="mg-btn-primary" href={`tel:${clinic.phone}`}>Call {clinic.phone}</a>}
              {clinic.email && <a className="mg-btn-secondary" href={`mailto:${clinic.email}`}>Email clinic</a>}
            </div>
          </div>
        ) : <>
        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {["Service", "Doctor", "Slot", "Details", "Done"].map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-mg-health text-white" : step === i + 1 ? "mg-gradient-primary text-white" : "bg-slate-100 text-slate-400"}`} data-testid={`step-${i + 1}`}>
                {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs ml-2 ${step === i + 1 ? "text-mg-navy font-bold" : "text-slate-400"}`}>{label}</span>
              {i < 4 && <div className={`flex-1 h-0.5 mx-2 ${step > i + 1 ? "bg-mg-health" : "bg-slate-100"}`} />}
            </div>
          ))}
        </div>

        <div className={embedded ? "bg-white" : "mg-card"}>
          {step === 1 && (
            <div data-testid="step-service">
              <h2 className="text-xl font-bold text-mg-navy mb-4">Choose a service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredServices.map((s) => (
                  <button key={s.service_id} onClick={() => { 
                    setServiceId(s.service_id); 
                    if (preselectedDoctorId) {
                      setStep(3); // skip doctor selection since it's preselected
                    } else {
                      setDoctorId(""); 
                      setStep(2); 
                    }
                  }}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${serviceId === s.service_id ? "border-mg-medical bg-blue-50" : "border-slate-100 hover:border-mg-medical"}`}
                    data-testid={`service-${s.service_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-mg-navy">{s.name}</div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_minutes} min</div>
                      </div>
                      <div className="font-bold text-mg-medical">₹{s.price}</div>
                    </div>
                    {s.description && <div className="text-xs text-slate-500 mt-2">{s.description}</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-doctor">
              <h2 className="text-xl font-bold text-mg-navy mb-4">Pick your doctor</h2>
              {filteredDoctors.length === 0 && <div className="text-sm text-slate-500">No doctors offer this service.</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredDoctors.map((d) => (
                  <button key={d.doctor_id} onClick={() => { setDoctorId(d.doctor_id); setStep(3); }}
                    className={`text-left p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${doctorId === d.doctor_id ? "border-mg-medical bg-blue-50" : "border-slate-100 hover:border-mg-medical"}`}
                    data-testid={`doctor-${d.doctor_id}`}>
                    <div className="w-12 h-12 mg-gradient-primary rounded-full flex items-center justify-center text-white font-bold">{d.name.charAt(3) || d.name.charAt(0)}</div>
                    <div>
                      <div className="font-bold text-mg-navy">{formatDoctorName(d.name, d.salutation)}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Stethoscope className="w-3 h-3" />{d.specialization}</div>
                      <div className="text-xs text-mg-medical font-semibold">Fee ₹{d.service_fees?.[serviceId] ?? selectedService?.price ?? 0}</div>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(1)} className="mt-4 text-sm text-slate-500 hover:text-mg-navy">← Back</button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-slot">
              <h2 className="text-xl font-bold text-mg-navy mb-4">Pick a date & time</h2>
              <input type="date" value={date} min={localCalendarDay(Date.now(), timezone)} onChange={(e) => setDate(e.target.value)} className="mg-input mb-4" data-testid="date-picker" />
              {loadingSlots && <div className="text-sm text-slate-500">Finding available slots…</div>}
              {!loadingSlots && slots.length === 0 && (
                <div className="text-sm text-slate-500 bg-amber-50 border border-amber-100 rounded-xl p-3">
                  No slots available for this day. Try another date.
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slots.map((s, i) => (
                  <button key={i} onClick={() => openPatientDetails(s)} className="border border-slate-200 hover:border-mg-medical rounded-xl py-2 text-sm font-semibold text-mg-navy hover:bg-blue-50 transition-colors" data-testid={`slot-${i}`}>
                    {formatSlotTime(s, timezone)}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">Times shown in clinic timezone: {timezone}</p>
              <button onClick={() => setStep(2)} className="mt-4 text-sm text-slate-500 hover:text-mg-navy">← Back</button>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-details">
              <h2 className="text-xl font-bold text-mg-navy mb-4">Your details</h2>
              <section aria-label="Selected appointment" className="mb-4 rounded-xl bg-blue-50 p-4 text-sm text-mg-navy">
                <p><strong>Service:</strong> {selectedService?.name}</p>
                <p><strong>Doctor:</strong> {formatDoctorName(selectedDoctor?.name, selectedDoctor?.salutation)}</p>
                <p><strong>When:</strong> {formatSlotDateTime(slot, timezone)} ({timezone})</p>
                <p><strong>Duration:</strong> {slotDurationMinutes(slot) ?? selectedService?.duration_minutes} minutes</p>
                <p><strong>Fee:</strong> ₹{selectedPrice}</p>
              </section>
              {selectedService?.service_mode === "hybrid" && <fieldset className="mb-4">
                <legend className="text-sm font-semibold text-mg-navy mb-2">Consultation type *</legend>
                <div className="flex gap-4">
                  <label><input type="radio" name="consultation-mode" value="in_person" checked={consultationMode === "in_person"} onChange={(e)=>setConsultationMode(e.target.value)} /> In person</label>
                  <label><input type="radio" name="consultation-mode" value="online" checked={consultationMode === "online"} onChange={(e)=>setConsultationMode(e.target.value)} /> Online</label>
                </div>
              </fieldset>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input className="mg-input" placeholder="Full name" value={pat.name} onChange={(e) => setPat({ ...pat, name: e.target.value })} required data-testid="patient-name" />
                <input className="mg-input" placeholder="Phone" value={pat.phone} onChange={(e) => setPat({ ...pat, phone: e.target.value })} required data-testid="patient-phone" />
                <input className="mg-input" placeholder="Email (optional)" value={pat.email} onChange={(e) => setPat({ ...pat, email: e.target.value })} data-testid="patient-email" />
                <select className="mg-input" value={pat.gender} onChange={(e) => setPat({ ...pat, gender: e.target.value })} data-testid="patient-gender">
                  <option value="">Select gender</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <label className="flex items-start gap-2 mt-4 text-xs text-slate-600">
                <input type="checkbox" checked={pat.consent} onChange={(e) => setPat({ ...pat, consent: e.target.checked })} data-testid="patient-consent" />
                <span>I consent to sharing my information with the clinic for appointment booking and to receive related communications. MediGenie doesn't provide medical advice — always consult a qualified doctor for clinical decisions.</span>
              </label>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setStep(3)} className="mg-btn-secondary">← Back</button>
                <button onClick={book} disabled={booking || !pat.name || !pat.phone || !pat.gender || !pat.consent || (selectedService?.service_mode === "hybrid" && !consultationMode)} className="mg-btn-primary flex-1 justify-center" data-testid="confirm-booking-btn">
                  {booking ? "Booking…" : "Confirm appointment"}
                </button>
              </div>
            </div>
          )}

          {step === 5 && confirmation && (
            <div className="text-center py-8" data-testid="step-confirmation">
              <Mascot size={96} className="mx-auto mg-float" />
              <h2 className="text-2xl font-extrabold text-mg-navy mt-4">You're booked! ✨</h2>
              <p className="text-slate-600 mt-1">We've reserved your appointment. The clinic will reach out if needed.</p>
              <AppointmentMessagingStatus delivery={confirmation.notification_delivery} />
              <div className="mt-6 p-4 bg-blue-50 rounded-xl text-sm text-mg-navy max-w-md mx-auto">
                <div><strong>Service:</strong> {services.find((s) => s.service_id === serviceId)?.name}</div>
                <div><strong>Doctor:</strong> {formatDoctorName(doctors.find((d) => d.doctor_id === doctorId)?.name, doctors.find((d) => d.doctor_id === doctorId)?.salutation)}</div>
                <div><strong>When:</strong> {formatSlotDateTime(slot, timezone)} ({timezone})</div>
                <div><strong>Fee:</strong> ₹{selectedPrice}</div>
                <div className="mt-1 text-xs text-slate-500">Reference: {confirmation.appointment_id}</div>
              </div>
            </div>
          )}
        </div>
        </>}
      </section>
    </div>
  );
}
